import React, { useEffect, useRef, useState, useCallback } from "react";
import { apiClient as axios, API } from "../api/client";
import { useAuth } from "../context/AuthContext";
import {
  listConversations,
  getConversation,
  getActiveId,
  setActiveId,
  upsertConversation,
  deleteConversation,
  createConversation,
  deriveTitle,
  togglePin,
} from "../api/conversations";

const SUPERSET_UI = process.env.REACT_APP_SUPERSET_URL || "http://localhost:8088";

const SUGGESTIONS = [
  "Show total sales by genre",
  "Bar chart of average sales by platform",
  "Top 10 publishers by global sales",
  "Line chart of sales over years",
  "Pie chart of regional sales distribution",
  // Multi-dataset prompts — these include keyword triggers ("combined", "across",
  // "compared to", "vs") that route to the SQL/multi-dataset path on the backend.
  "Join sales and user scores by publisher and create a bar chart",
  "Combine sales and user score across genres",
  "Compare critic score vs user score across publishers",
];

// Feedback categories (used in the Feedback modal + admin review badges)
const FB_CATEGORIES = [
  { key: "bug", label: "Something's broken", short: "Bug", cls: "rose",
    icon: <><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></> },
  { key: "strategy", label: "Strategy / direction", short: "Strategy", cls: "indigo",
    icon: <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></> },
  { key: "idea", label: "Idea / request", short: "Idea", cls: "amber",
    icon: <><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V17h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" /></> },
  { key: "other", label: "Other", short: "Other", cls: "slate",
    icon: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></> },
];
const FB_CLS = {
  rose: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  slate: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};
const fbCat = (k) => FB_CATEGORIES.find((c) => c.key === k) || FB_CATEGORIES[3];

// Fetches an admin-only feedback attachment (with auth) and renders it inline.
function FbMedia({ att }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj = null;
    axios
      .get(`${API}/api/feedback/attachment/${att.id}`, { responseType: "blob" })
      .then((res) => { obj = URL.createObjectURL(res.data); setUrl(obj); })
      .catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [att.id]);
  if (!url) return <div className="h-28 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />;
  return att.is_video ? (
    <video src={url} controls className="w-full max-h-56 rounded-lg bg-black" />
  ) : (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={att.filename} className="w-full max-h-56 object-cover rounded-lg border border-slate-200 dark:border-slate-700" />
    </a>
  );
}

export default function VizauraApp() {
  const { user, logout } = useAuth();

  // Layout
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [vizExpanded, setVizExpanded] = useState(true);
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("vizaura_theme") === "dark"; } catch { return false; }
  });

  // Chat
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");

  // Conversation tracking — server-side state lives under this id, but the
  // browser also keeps a richer index so users can browse past chats.
  const [conversationId, setConversationId] = useState(() => {
    const existing = getActiveId();
    if (existing) return existing;
    return createConversation();
  });
  const [conversations, setConversations] = useState(() => listConversations());

  const refreshConversations = useCallback(() => {
    setConversations(listConversations());
  }, []);

  function startNewConversation() {
    const fresh = createConversation();
    setConversationId(fresh);
    setMessages([]);
    setChartUrl("");
    setContext({ title: "", subtitle: "" });
    // New session defaults: dataset "+ Create New", chart "Create New", no active chart.
    setDatasetMode("create_new");
    setChartMode("create");
    setActiveChartId(null);
    setSelectedProjectId(null); // All Projects
    setAppMode("chart");
    restoreRef.current = null;
    refreshConversations();
  }

  function switchConversation(id) {
    if (!id || id === conversationId) return;
    const stored = getConversation(id);
    setActiveId(id);
    setConversationId(id);
    setMessages(stored?.messages || []);
    setChartUrl(stored?.chartUrl || "");
    setContext({ title: stored?.title || "", subtitle: stored?.chartSubtitle || "" });
    // Restore this chat's selections (#20). Modes are safe to set directly; the
    // dataset/project depend on lists that reload when the DB changes, so we stash
    // them and re-apply once those lists arrive.
    setChartMode(stored?.chartMode || "create");
    setActiveChartId(stored?.activeChartId ?? null);
    setDatasetMode(stored?.datasetMode || "create_new");
    restoreRef.current = { datasetId: stored?.datasetId ?? null, projectId: stored?.projectId ?? null };
    if (stored?.dbId) setSelectedDbId(stored.dbId);
    refreshConversations();
  }

  function pinConversation(id) {
    togglePin(id);
    refreshConversations();
  }

  function removeConversation(id) {
    deleteConversation(id);
    if (id === conversationId) {
      // Removed the active one — start a fresh chat.
      startNewConversation();
    } else {
      refreshConversations();
    }
  }

  // Data context
  const [dbs, setDbs] = useState([]);
  const [dboards, setDboards] = useState([]);
  const [datasets, setDatasets] = useState([]);
  // Projects (from projects_current on the selected DB). null = "All Projects".
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedDbId, setSelectedDbId] = useState(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);

  // Active Superset connection (Connections panel can switch it at runtime)
  const [activeConn, setActiveConn] = useState(null); // { base_url, username, custom }
  const [connForm, setConnForm] = useState({ base_url: "", username: "admin", password: "admin" });
  const [connBusy, setConnBusy] = useState(false);
  const [dataRefreshKey, setDataRefreshKey] = useState(0); // bump to re-pull dbs/dashboards

  // Admin panel (access management) — admin-only
  const [adminData, setAdminData] = useState({ allowed_emails: [], users: [] });
  const [adminEmail, setAdminEmail] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminActivity, setAdminActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Feedback
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [fbView, setFbView] = useState("compose"); // compose | list (admin)
  const [fbCategory, setFbCategory] = useState("bug");
  const [fbMessage, setFbMessage] = useState("");
  const [fbFiles, setFbFiles] = useState([]); // { file, url, isVideo }
  const [fbBusy, setFbBusy] = useState(false);
  const [fbSent, setFbSent] = useState(false);
  const [fbDrag, setFbDrag] = useState(false);
  const [fbList, setFbList] = useState([]);
  const [fbListLoading, setFbListLoading] = useState(false);
  const fbInputRef = useRef(null);

  // Dataset Mode (top selector) — "existing" locks to the chosen dataset,
  // "create_new" always builds a brand-new dataset for the request.
  // New sessions default to "+ Create New" so Vizaura never assumes an existing
  // dataset; the user can pick an existing one from the dropdown if they want.
  const [datasetMode, setDatasetMode] = useState("create_new");
  // Chart Mode (near the prompt) — "create" makes a new chart each time,
  // "update" edits the active chart in place.
  const [chartMode, setChartMode] = useState("create");
  // App mode: "chart" = build/update charts; "qa" = answer questions about the session.
  const [appMode, setAppMode] = useState("chart");
  // The chart currently shown in the iframe (target of "update" requests).
  const [activeChartId, setActiveChartId] = useState(null);

  // Chart embed
  const [chartUrl, setChartUrl] = useState("");
  const [context, setContext] = useState({ title: "", subtitle: "" });
  const [chartLoading, setChartLoading] = useState(false);

  // UI state
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const chatLogRef = useRef(null);
  const inputRef = useRef(null);
  // Pending per-chat restore: {datasetId, projectId} to re-apply after the
  // dataset/project lists reload (so reopening a chat keeps its selections).
  const restoreRef = useRef(null);
  const chatAbortRef = useRef(null);  // AbortController for the in-flight /api/chat request

  // ── Helpers ──
  const prettyErr = (e) => {
    if (e?.response?.data?.detail) return String(e.response.data.detail);
    if (e?.response?.data) return JSON.stringify(e.response.data);
    return e?.message || String(e);
  };

  const showToast = useCallback((msg, type = "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // ── Theme ──
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    try { localStorage.setItem("vizaura_theme", isDark ? "dark" : "light"); } catch { /* ignore */ }
  }, [isDark]);

  // Scroll chat
  useEffect(() => {
    if (chatLogRef.current) {
      chatLogRef.current.scrollTo({ top: chatLogRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  // Persist the active conversation locally whenever its content changes,
  // so refreshing the page or switching chats restores everything.
  useEffect(() => {
    if (!conversationId) return;
    upsertConversation({
      id: conversationId,
      title: deriveTitle(messages),
      messages,
      chartUrl,
      chartSubtitle: context.subtitle,
      // Per-chat selections so reopening a chat restores its context (#20).
      dbId: selectedDbId,
      datasetMode,
      datasetId: selectedDatasetId,
      projectId: selectedProjectId,
      chartMode,
      activeChartId,
    });
    refreshConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messages, chartUrl, context.subtitle, selectedDbId, datasetMode, selectedDatasetId, selectedProjectId, chartMode, activeChartId]);

  // ── Backend wiring ──
  useEffect(() => {
    (async () => {
      try {
        const [dbRes, dashRes] = await Promise.all([
          axios.get(`${API}/api/databases`),
          axios.get(`${API}/api/dashboards`),
        ]);
        setDbs(dbRes.data || []);
        setDboards(dashRes.data || []);
        if (dbRes.data?.length) setSelectedDbId(dbRes.data[0].id);
      } catch (err) {
        showToast(prettyErr(err));
      }
    })();
  }, [showToast, dataRefreshKey]);

  // Track the active Superset's public URL so chart links point at the connected
  // instance (which may be a client's Superset, not the bundled one).
  useEffect(() => {
    axios
      .get(`${API}/api/connections/superset`)
      .then((res) => setActiveConn(res.data))
      .catch(() => {});
  }, [dataRefreshKey]);

  useEffect(() => {
    if (!selectedDbId) return;
    (async () => {
      try {
        setDatasets([]);
        setSelectedDatasetId(null);
        const res = await axios.get(`${API}/api/datasets?database_id=${selectedDbId}`);
        const list = res.data || [];
        setDatasets(list);
        const wantDs = restoreRef.current && restoreRef.current.datasetId;
        if (wantDs && list.some((d) => d.id === wantDs)) {
          setSelectedDatasetId(wantDs);       // restore the chat's dataset
          setDatasetMode("existing");
        } else if (list.length) {
          setSelectedDatasetId(list[0].id);   // background default (dropdown shows Create New)
        }
      } catch (err) {
        showToast(prettyErr(err));
      }
    })();
  }, [selectedDbId, showToast]);

  // Projects follow the selected database — populate the Project dropdown.
  useEffect(() => {
    if (!selectedDbId) { setProjects([]); return; }
    (async () => {
      try {
        setSelectedProjectId(null); // default to "All Projects" when DB changes
        const res = await axios.get(`${API}/api/projects?database_id=${selectedDbId}`);
        const list = res.data?.projects || [];
        setProjects(list);
        const wantP = restoreRef.current && restoreRef.current.projectId;
        if (wantP && list.some((p) => p.id === wantP)) setSelectedProjectId(wantP); // restore chat's project
      } catch {
        setProjects([]); // no projects_current on this DB — just show "All Projects"
      }
    })();
  }, [selectedDbId]);

  // Rename a project (display-only alias; the source zedoc name is untouched).
  async function renameProject(id) {
    if (!id) { showToast("Pick a project to rename first"); return; }
    const current = projects.find((p) => p.id === id)?.name || "";
    const name = window.prompt("Rename this programme (display only — leave blank to reset):", current);
    if (name === null) return;
    try {
      await axios.put(`${API}/api/projects/${encodeURIComponent(id)}/alias`, { alias: name.trim() });
      const res = await axios.get(`${API}/api/projects?database_id=${selectedDbId}`);
      setProjects(res.data?.projects || []);
      showToast(name.trim() ? "Project name updated" : "Project name reset");
    } catch (e) {
      showToast(prettyErr(e));
    }
  }

  // Auto-expand the chat textarea to fit its content (up to a cap, then scroll).
  // Runs on every input change — including when it's cleared after send.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const MAX = 200; // ~8 lines, then the box scrolls internally
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, MAX) + "px";
    el.style.overflowY = el.scrollHeight > MAX ? "auto" : "hidden";
  }, [input]);

  // ── Chat logic ──
  function addMessage(sender, text, extra = {}) {
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), sender, text, ...extra }]);
  }

  function stopChat() {
    try { chatAbortRef.current?.abort(); } catch { /* ignore */ }
  }

  async function sendToChat(promptText, overrides = {}) {
    if (!selectedDbId) { showToast("Select a database first"); return; }
    const controller = new AbortController();
    chatAbortRef.current = controller;
    setBusy(true);
    if (appMode !== "qa") setChartLoading(true); // Q&A produces no chart — no spinner

    try {
      const res = await axios.post(`${API}/api/chat`, {
        prompt: promptText,
        database_id: selectedDbId,
        schema: "",
        conversation_id: conversationId,
        // Dataset Mode: lock to the selected dataset, or force a brand-new one.
        dataset_mode: datasetMode,
        dataset_id: datasetMode === "existing" ? selectedDatasetId : null,
        // Chart Mode: create a new chart, or update the active one.
        chart_mode: chartMode,
        active_chart_id: chartMode === "update" ? activeChartId : null,
        // Project context: when a specific project is selected, its ID is
        // auto-applied as a filter (null = All Projects, no filter). A chip
        // click can pass an explicit projectId override so the re-run uses the
        // just-picked project without waiting for React state to settle.
        project_id: (overrides.projectId !== undefined ? overrides.projectId : selectedProjectId) || null,
        // Q&A mode: answer from session context instead of building a chart.
        qa_mode: appMode === "qa",
      }, { signal: controller.signal });

      // Server may have minted a fresh id for us — keep it in sync.
      if (res.data?.conversation_id && res.data.conversation_id !== conversationId) {
        setConversationId(res.data.conversation_id);
        setActiveId(res.data.conversation_id);
      }

      // Track the active chart so follow-up "update" requests target it.
      if (res.data?.chart_id) setActiveChartId(res.data.chart_id);

      // After a chart is successfully CREATED, auto-switch the toggle to "Update"
      // so the next prompt refines this chart rather than making a new one. The
      // user can switch back to "Create New" manually anytime.
      if (res.data?.action === "created" && res.data?.chart_id) setChartMode("update");

      if (res.data?.chart_url) {
        const rawUrl = res.data.chart_url;
        const sliceMatch = rawUrl.match(/\/slice\/(\d+)/);
        // Use the origin of the returned URL — i.e. the *connected* Superset —
        // rather than the build-time constant, so charts from any connected
        // Superset (e.g. a client's) resolve to the right host.
        let origin = SUPERSET_UI;
        try { origin = new URL(rawUrl).origin; } catch { /* keep fallback */ }
        // Cache-bust the src so the iframe reloads even when the slice id is
        // unchanged (i.e. after an in-place update).
        const bust = `&_=${Date.now()}`;
        const embedUrl = sliceMatch
          ? `${origin}/superset/explore/?slice_id=${sliceMatch[1]}&standalone=true&height=640${bust}`
          : rawUrl;
        setChartUrl(embedUrl);
        setContext({
          title: res.data.response || "Chart",
          subtitle: res.data.action === "modified" ? "Updated existing chart" : rawUrl,
        });
        if (!vizExpanded) setVizExpanded(true);
        addMessage("bot", res.data.response || "Chart created!", {
          suggestions: res.data?.suggestions || [],
          settingChanges: res.data?.setting_changes || [],
        });
      } else {
        // Friendly outcomes (missing_data, no_active_chart, needs_clarification,
        // Q&A answers) or plain replies — show the message + any agent chips and
        // leave the current chart untouched.
        addMessage("bot", res.data?.response || "Hmm, no chart URL came back from the server.", {
          suggestions: res.data?.suggestions || [],
          settingChanges: res.data?.setting_changes || [],
        });
      }
    } catch (e) {
      // User-initiated cancel — quiet, no error toast.
      if (axios.isCancel?.(e) || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        addMessage("bot", "Stopped. Ask me something else when you're ready.");
      } else {
        showToast(prettyErr(e));
        addMessage("bot", "Something went wrong — check the error toast for details.");
      }
    } finally {
      chatAbortRef.current = null;
      setBusy(false);
      setChartLoading(false);
    }
  }

  function handleSend(text) {
    const msg = (text || input).trim();
    if (!msg) return;
    addMessage("user", msg);
    setInput("");
    inputRef.current?.focus();
    setTimeout(() => sendToChat(msg), 80);
  }

  // Agent "choose a programme" button: select the project in the dropdown AND
  // re-run the user's original request (with an explicit project override so it
  // doesn't wait for state to settle). Fixes the no-project clarification loop.
  function pickProjectAndResend(projectId, projectName) {
    if (appMode !== "chart") setAppMode("chart");
    setSelectedProjectId(projectId);
    restoreRef.current = null; // explicit user selection
    const lastUser = [...messages].reverse().find((m) => m.sender === "user")?.text;
    addMessage("user", `Use ${projectName}`);
    if (lastUser) setTimeout(() => sendToChat(lastUser, { projectId }), 80);
  }

  // ── Overlay panels ──
  const [overlay, setOverlay] = useState({ open: false, title: "", content: "" });

  function openSidePanel(type) {
    const title = type === "connections" ? "Connections" : type === "admin" ? "Admin"
      : type === "help" ? "Help & Release Notes" : "Settings";

    if (type === "connections") {
      // Interactive React panel — fetch the active connection to prefill.
      setOverlay({ open: true, title, type, content: "" });
      (async () => {
        try {
          const res = await axios.get(`${API}/api/connections/superset`);
          setActiveConn(res.data);
          setConnForm((f) => ({
            ...f,
            base_url: res.data.base_url || "",
            username: res.data.username || "admin",
          }));
        } catch {
          /* leave form as-is */
        }
      })();
      return;
    }

    if (type === "admin") {
      setOverlay({ open: true, title, type, content: "" });
      loadAdmin();
      loadActivity();
      return;
    }

    // Settings is a real React panel (see the overlay render) — no HTML string.
    setOverlay({ open: true, title, type, content: "" });
  }

  // ── Admin: access management ──
  async function loadAdmin() {
    try {
      const res = await axios.get(`${API}/api/admin/access`);
      setAdminData(res.data || { allowed_emails: [], users: [] });
    } catch (e) {
      showToast(prettyErr(e));
    }
  }
  async function loadActivity() {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/activity`);
      setAdminActivity(res.data?.activity || []);
    } catch (e) {
      /* non-fatal — activity log is best-effort */
    } finally {
      setActivityLoading(false);
    }
  }
  async function addAllowedEmail() {
    const email = adminEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return showToast("Enter a valid email");
    setAdminBusy(true);
    try {
      await axios.post(`${API}/api/admin/access/allow`, { email });
      setAdminEmail("");
      showToast(`Allowed ${email}`, "success");
      loadAdmin();
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setAdminBusy(false);
    }
  }
  async function removeAllowedEmail(email) {
    setAdminBusy(true);
    try {
      await axios.delete(`${API}/api/admin/access/allow/${encodeURIComponent(email)}`);
      loadAdmin();
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setAdminBusy(false);
    }
  }
  async function deleteUserAccount(id) {
    setAdminBusy(true);
    try {
      await axios.delete(`${API}/api/admin/access/user/${id}`);
      showToast("User removed", "success");
      loadAdmin();
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setAdminBusy(false);
    }
  }

  // ── Feedback ──
  const FB_MAX_MB = 50;
  const FB_MAX_FILES = 6;
  function openFeedback() {
    setFbView("compose");
    setFbSent(false);
    setFeedbackOpen(true);
  }
  function closeFeedback() {
    fbFiles.forEach((f) => URL.revokeObjectURL(f.url));
    setFbFiles([]);
    setFbMessage("");
    setFbCategory("bug");
    setFbSent(false);
    setFbView("compose");
    setFeedbackOpen(false);
  }
  function addFbFiles(list) {
    const incoming = Array.from(list || []);
    setFbFiles((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (!/^(image|video)\//.test(file.type)) { showToast("Only images and videos can be attached"); continue; }
        if (file.size > FB_MAX_MB * 1024 * 1024) { showToast(`${file.name} is over ${FB_MAX_MB} MB`); continue; }
        if (next.length >= FB_MAX_FILES) { showToast(`Up to ${FB_MAX_FILES} files`); break; }
        next.push({ file, url: URL.createObjectURL(file), isVideo: file.type.startsWith("video/") });
      }
      return next;
    });
  }
  function removeFbFile(i) {
    setFbFiles((prev) => {
      if (prev[i]) URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, idx) => idx !== i);
    });
  }
  async function submitFeedback() {
    if (!fbMessage.trim()) return showToast("Please write a short message");
    setFbBusy(true);
    try {
      const fd = new FormData();
      fd.append("message", fbMessage.trim());
      fd.append("category", fbCategory);
      fbFiles.forEach((f) => fd.append("files", f.file));
      await axios.post(`${API}/api/feedback`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      fbFiles.forEach((f) => URL.revokeObjectURL(f.url));
      setFbFiles([]);
      setFbMessage("");
      setFbSent(true);
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setFbBusy(false);
    }
  }
  async function loadFeedbackList() {
    setFbListLoading(true);
    try {
      const res = await axios.get(`${API}/api/feedback`);
      setFbList(res.data?.feedback || []);
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setFbListLoading(false);
    }
  }
  async function toggleReviewed(id) {
    try {
      const res = await axios.post(`${API}/api/feedback/${id}/reviewed`);
      setFbList((p) => p.map((x) => (x.id === id ? { ...x, status: res.data.status } : x)));
    } catch (e) {
      showToast(prettyErr(e));
    }
  }
  async function closeFeedbackTicket(id) {
    try {
      const res = await axios.post(`${API}/api/feedback/${id}/close`);
      setFbList((p) => p.map((x) => (x.id === id ? { ...x, status: res.data.status } : x)));
      showToast(res.data?.emailed ? "Closed — the author was emailed" : "Closed (no email sent)", "success");
    } catch (e) {
      showToast(prettyErr(e));
    }
  }
  async function removeFeedback(id) {
    try {
      await axios.delete(`${API}/api/feedback/${id}`);
      setFbList((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      showToast(prettyErr(e));
    }
  }
  const fbFmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  // ── Superset connection actions ──
  async function testSupersetConn() {
    if (!connForm.base_url.trim()) return showToast("Enter a Superset URL first");
    setConnBusy(true);
    try {
      const res = await axios.post(`${API}/api/connections/superset/test`, connForm);
      showToast(res.data.message || "Reachable", "success");
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setConnBusy(false);
    }
  }

  async function connectSuperset() {
    if (!connForm.base_url.trim()) return showToast("Enter a Superset URL first");
    setConnBusy(true);
    try {
      const res = await axios.post(`${API}/api/connections/superset`, connForm);
      setActiveConn({ base_url: res.data.base_url, username: connForm.username, custom: true });
      showToast(res.data.message || "Connected", "success");
      setDataRefreshKey((k) => k + 1); // re-pull databases/dashboards from the new Superset
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setConnBusy(false);
    }
  }

  async function resetSuperset() {
    setConnBusy(true);
    try {
      const res = await axios.post(`${API}/api/connections/superset/reset`);
      setActiveConn({ base_url: res.data.base_url, username: "admin", custom: false });
      setConnForm((f) => ({ ...f, base_url: res.data.base_url, username: "admin", password: "admin" }));
      showToast(res.data.message || "Reset to default", "success");
      setDataRefreshKey((k) => k + 1);
    } catch (e) {
      showToast(prettyErr(e));
    } finally {
      setConnBusy(false);
    }
  }

  // ── Render ──
  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-slate-800 dark:text-slate-100 transition-colors duration-300 overflow-hidden">

      {/* Inline styles */}
      <style>{`
        .transition-size { transition: width 300ms cubic-bezier(.4,0,.2,1), max-width 300ms cubic-bezier(.4,0,.2,1) }
        @keyframes msg-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
        .msg-enter { animation: msg-in .25s ease-out }
        @keyframes fade-in { from { opacity:0 } to { opacity:1 } }
        .fade-in { animation: fade-in .2s ease-out }
        @keyframes toast-in { from { opacity:0; transform:translateY(-12px) } to { opacity:1; transform:translateY(0) } }
        .toast-enter { animation: toast-in .3s ease-out }
        .chat-scroll::-webkit-scrollbar { width: 6px }
        .chat-scroll::-webkit-scrollbar-track { background: transparent }
        .chat-scroll::-webkit-scrollbar-thumb { background: rgba(148,163,184,.25); border-radius: 3px }
        .chat-scroll::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,.45) }
      `}</style>

      {/* ── Toast ── */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 toast-enter">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border backdrop-blur-md ${
            toast.type === "error"
              ? "bg-red-50/90 dark:bg-red-900/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
              : "bg-emerald-50/90 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
          }`}>
            <span className="text-sm font-medium max-w-[400px] truncate">{toast.msg}</span>
            <button onClick={() => setToast(null)} className="opacity-60 hover:opacity-100 transition">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header className="shrink-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800 z-30">
        <div className="px-4 sm:px-5 h-14 flex items-center justify-between gap-4">
          {/* Left */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setSidebarExpanded((v) => !v)}
              className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition"
              aria-label="Toggle sidebar"
            >
              <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-sm shadow-indigo-500/20">
                <svg width="14" height="14" viewBox="0 0 24 24" className="text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h2l2-6 2 4 2-7 2 9 2-5 2 5h2"/></svg>
              </div>
              <span className="font-semibold text-[15px] tracking-tight">Vizaura</span>
            </div>
          </div>

          {/* Center — selectors */}
          <div className="hidden md:flex items-center gap-2 flex-1 justify-center">
            <div className="flex items-center gap-2 bg-slate-100/80 dark:bg-slate-800/80 rounded-lg px-1 py-1">
              <select
                value={selectedDbId || ""}
                onChange={(e) => { restoreRef.current = null; setSelectedDbId(Number(e.target.value)); }}
                className="bg-transparent text-sm px-2 py-1 rounded-md focus:outline-none cursor-pointer text-slate-600 dark:text-slate-300"
              >
                {dbs.map((db) => (
                  <option key={db.id} value={db.id}>{db.database_name}</option>
                ))}
              </select>
              <div className="w-px h-4 bg-slate-300 dark:bg-slate-600" />
              <select
                value={datasetMode === "create_new" ? "__new__" : (selectedDatasetId || "")}
                onChange={(e) => {
                  restoreRef.current = null;
                  const v = e.target.value;
                  if (v === "__new__") {
                    setDatasetMode("create_new");
                  } else {
                    setDatasetMode("existing");
                    setSelectedDatasetId(Number(v));
                  }
                }}
                title={datasetMode === "create_new"
                  ? "A new dataset will be created for each request"
                  : "Charts are locked to this dataset"}
                className="bg-transparent text-sm px-2 py-1 rounded-md max-w-[220px] focus:outline-none cursor-pointer text-slate-600 dark:text-slate-300"
              >
                <option value="__new__">+ Create New</option>
                {datasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {(ds.schema ? ds.schema + "." : "") + ds.table_name}
                  </option>
                ))}
              </select>
              <div className="w-px h-4 bg-slate-300 dark:bg-slate-600" />
              {/* Project — auto-filters charts by projectid (All Projects = no filter) */}
              <select
                value={selectedProjectId || ""}
                onChange={(e) => { restoreRef.current = null; setSelectedProjectId(e.target.value || null); }}
                title={selectedProjectId
                  ? "Charts are automatically filtered to this project"
                  : "No project filter — all projects"}
                className="bg-transparent text-sm px-2 py-1 rounded-md max-w-[200px] focus:outline-none cursor-pointer text-slate-600 dark:text-slate-300"
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {selectedProjectId && (
                <button
                  onClick={() => renameProject(selectedProjectId)}
                  title="Rename this programme (display only)"
                  className="shrink-0 text-slate-400 hover:text-indigo-500 transition"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                </button>
              )}
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500 mr-1">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />{dbs.length} DB{dbs.length !== 1 && "s"}</span>
              <span className="opacity-40">·</span>
              <span>{datasets.length} sets</span>
              <span className="opacity-40">·</span>
              <span>{dboards.length} dash</span>
            </div>
            <button
              onClick={startNewConversation}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-300 transition"
              title="Start a fresh conversation"
            >
              + New chat
            </button>
            {user ? (
              <div className="hidden sm:flex items-center gap-2 ml-1">
                <span className="text-[11px] text-slate-500 dark:text-slate-400">{user.username}</span>
                <button
                  onClick={logout}
                  className="text-[11px] text-slate-400 hover:text-red-500 transition"
                >
                  Sign out
                </button>
              </div>
            ) : null}
            <button
              onClick={() => setIsDark((v) => !v)}
              className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Sidebar ── */}
        <aside className={`transition-all duration-300 ease-out bg-white/60 dark:bg-slate-900/60 backdrop-blur-lg border-r border-slate-200/80 dark:border-slate-800 shrink-0 overflow-hidden flex flex-col ${
          sidebarExpanded ? "w-60" : "w-0 md:w-16"
        }`}>
          <nav className="flex-1 flex flex-col min-w-[64px]">
            {sidebarExpanded && (
              <div className="px-4 pt-5 pb-2">
                <p className="text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-medium">Menu</p>
              </div>
            )}
            <ul className={`${sidebarExpanded ? "px-3" : "px-2"} space-y-0.5 mt-1`}>
              {[
                ...(user?.is_admin
                  ? [{ key: "admin", label: "Admin", icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></> }]
                  : []),
                { key: "help", label: "Help & Notes", icon: <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></> },
                { key: "feedback", label: "Feedback", icon: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></> },
                { key: "connections", label: "Connections", icon: <><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 10-7.07-7.07l-1 1"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 107.07 7.07l1-1"/></> },
                { key: "settings", label: "Settings", icon: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9c0 .66.26 1.3.73 1.77.47.47 1.11.73 1.77.73h.09a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></> },
              ].map((item) => (
                <li key={item.key}>
                  <button
                    onClick={() => (item.key === "feedback" ? openFeedback() : openSidePanel(item.key))}
                    className={`w-full flex items-center gap-3 rounded-lg transition ${
                      sidebarExpanded ? "px-3 py-2.5" : "px-0 py-2.5 justify-center"
                    } hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400`}
                    title={item.label}
                  >
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
                    </span>
                    {sidebarExpanded && <span className="text-sm">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>

            {/* History */}
            {sidebarExpanded && (
              <div className="mt-4 flex-1 flex flex-col min-h-0">
                <div className="px-4 pb-2 flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-medium">History</p>
                  <button
                    onClick={startNewConversation}
                    className="text-[11px] text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 transition"
                    title="Start a new chat"
                  >
                    + New
                  </button>
                </div>
                <div className="flex-1 overflow-auto px-2 chat-scroll">
                  {conversations.length === 0 && (
                    <p className="px-3 py-2 text-[11px] text-slate-400 dark:text-slate-500">No past chats yet.</p>
                  )}
                  {conversations.map((c) => {
                    const active = c.id === conversationId;
                    return (
                      <div
                        key={c.id}
                        className={`group flex items-center justify-between gap-1 px-2 py-1.5 rounded-lg transition cursor-pointer ${
                          active
                            ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                            : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`}
                        onClick={() => switchConversation(c.id)}
                        title={c.title || "New chat"}
                      >
                        {c.pinned && (
                          <svg className="w-3 h-3 shrink-0 text-indigo-500 dark:text-indigo-400" viewBox="0 0 24 24" fill="currentColor" title="Pinned"><path d="M14 2l1 6 4 3-5 1-1 8-2-7-6-2 6-3 1-6z"/></svg>
                        )}
                        <span className="text-[12px] truncate flex-1">{c.title || "New chat"}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            pinConversation(c.id);
                          }}
                          className={`${c.pinned ? "opacity-100 text-indigo-500 dark:text-indigo-400" : "opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-500"} transition shrink-0`}
                          title={c.pinned ? "Unpin chat" : "Pin chat"}
                        >
                          <svg className="w-3.5 h-3.5" fill={c.pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M14 2l1 6 4 3-5 1-1 8-2-7-6-2 6-3 1-6z"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeConversation(c.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition shrink-0"
                          title="Delete chat"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sidebar footer */}
            {sidebarExpanded && (
              <div className="mt-auto px-3 py-4 shrink-0">
                <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 border border-indigo-100/80 dark:border-indigo-900/40 p-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400 mb-2.5">Try asking</p>
                  <div className="space-y-1.5">
                    {SUGGESTIONS.slice(0, 3).map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSend(s)}
                        title={s}
                        className="block w-full text-left text-xs text-indigo-500/80 dark:text-indigo-400/70 hover:text-indigo-700 dark:hover:text-indigo-300 transition truncate"
                      >
                        &rarr; {s}
                      </button>
                    ))}
                    {SUGGESTIONS.slice(5).map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSend(s)}
                        className="block w-full text-left text-xs text-violet-500/80 dark:text-violet-400/70 hover:text-violet-700 dark:hover:text-violet-300 transition truncate"
                        title={s}
                      >
                        &rarr; {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 flex overflow-hidden">

          {/* ── Chat ── */}
          <section className="flex-1 flex flex-col min-w-0">
            {/* Chat header */}
            <div className="shrink-0 px-6 py-3 border-b border-slate-200/80 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold">Chat</h2>
                  <p className="text-xs text-slate-400 dark:text-slate-500">Describe what you want to visualize</p>
                </div>
                <button
                  onClick={() => setVizExpanded((v) => !v)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    {vizExpanded
                      ? <><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></>
                      : <><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></>
                    }
                  </svg>
                  {vizExpanded ? "Hide chart" : "Show chart"}
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={chatLogRef} className="flex-1 overflow-auto px-4 sm:px-6 py-5 space-y-4 chat-scroll">

              {/* Welcome (show when empty) */}
              {messages.length === 0 && (
                <div className="fade-in flex flex-col items-center justify-center h-full text-center px-4 -mt-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mb-5 shadow-lg shadow-indigo-500/20">
                    <svg width="24" height="24" viewBox="0 0 24 24" className="text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h2l2-6 2 4 2-7 2 9 2-5 2 5h2"/></svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-1.5">What would you like to visualize?</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-6">
                    Describe a chart in natural language and I'll create it for you in Superset.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSend(s)}
                        title={s}
                        className="px-3.5 py-2 rounded-full text-xs font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300 dark:hover:border-indigo-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all shadow-sm hover:shadow"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              {messages.map((m) => (
                <div key={m.id} className={`msg-enter flex gap-3 ${m.sender === "user" ? "justify-end" : ""}`}>
                  {m.sender === "bot" && (
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-indigo-500/20">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h2l2-6 2 4 2-7 2 9 2-5 2 5h2"/></svg>
                    </div>
                  )}
                  <div className={`max-w-[60ch] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                    m.sender === "user"
                      ? "bg-indigo-600 text-white rounded-br-md shadow-sm shadow-indigo-600/20"
                      : "bg-white dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700/80 rounded-bl-md shadow-sm"
                  }`}>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    {m.sender === "bot" && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {m.suggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => { setAppMode("chart"); handleSend(s); }}
                            className="text-left text-[12px] px-2.5 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.sender === "bot" && Array.isArray(m.settingChanges) && m.settingChanges.length > 0 && (
                      <div className="mt-2.5">
                        {m.settingChanges.some((sc) => sc.setting === "project") && (
                          <div className="flex flex-wrap gap-1.5">
                            {m.settingChanges.filter((sc) => sc.setting === "project").map((sc, i) => (
                              <button
                                key={i}
                                onClick={() => pickProjectAndResend(sc.value, sc.why)}
                                className="text-left text-[12px] px-2.5 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors"
                              >
                                Use {sc.why}
                              </button>
                            ))}
                          </div>
                        )}
                        {m.settingChanges.filter((sc) => sc.setting !== "project").length > 0 && (
                          <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 space-y-0.5">
                            {m.settingChanges.filter((sc) => sc.setting !== "project").map((sc, i) => (
                              <div key={i}>
                                Tip: set <span className="font-medium">{sc.setting}</span>
                                {sc.value ? <> to <span className="font-medium">{sc.value}</span></> : null}
                                {sc.why ? <> — {sc.why}</> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {m.sender === "user" && (
                    <div className="w-7 h-7 rounded-lg bg-slate-700 dark:bg-slate-600 text-white flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-bold">U</div>
                  )}
                </div>
              ))}

              {/* Typing indicator */}
              {busy && (
                <div className="msg-enter flex gap-3">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center shrink-0 mt-0.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17h2l2-6 2 4 2-7 2 9 2-5 2 5h2"/></svg>
                  </div>
                  <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 shadow-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="shrink-0 px-4 sm:px-6 py-3 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm border-t border-slate-200/80 dark:border-slate-800">
              {/* App mode: build charts vs. ask questions about the session */}
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <div className="inline-flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => setAppMode("chart")}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                      appMode === "chart"
                        ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    }`}
                  >Chart Creation</button>
                  <button
                    type="button"
                    onClick={() => setAppMode("qa")}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                      appMode === "qa"
                        ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    }`}
                  >Q&amp;A</button>
                </div>
                {appMode === "chart" ? (
                  <>
                    <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">Chart mode</span>
                    <div className="inline-flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setChartMode("create")}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                          chartMode === "create"
                            ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm"
                            : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        }`}
                      >Create new</button>
                      <button
                        type="button"
                        onClick={() => setChartMode("update")}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                          chartMode === "update"
                            ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm"
                            : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        }`}
                      >Update</button>
                    </div>
                    {chartMode === "update" && (
                      <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                        {activeChartId ? `editing chart #${activeChartId}` : "no active chart yet"}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    Answers questions about this session — no chart is created
                  </span>
                )}
              </div>
              <div className="relative">
                <textarea
                  ref={inputRef}
                  rows={1}
                  placeholder={appMode === "qa"
                    ? "Ask a question about this session…"
                    : (chartMode === "update" ? "Describe a change to the chart..." : "Describe a chart...")}
                  className="w-full resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 pl-4 pr-12 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 dark:focus:border-indigo-600 shadow-sm transition"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                  }}
                />
                {busy ? (
                  <button
                    onClick={stopChat}
                    title="Stop"
                    className="absolute right-2 bottom-2 w-8 h-8 rounded-lg bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center transition-all"
                  >
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                  </button>
                ) : (
                  <button
                    onClick={() => handleSend()}
                    disabled={!input.trim()}
                    className="absolute right-2 bottom-2 w-8 h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white flex items-center justify-center transition-all disabled:scale-95"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 -rotate-45" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                  </button>
                )}
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px]">Enter</kbd> to send · <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[10px]">Shift+Enter</kbd> new line
              </p>
            </div>
          </section>

          {/* ── Viz pane ── */}
          <aside className={`relative border-l border-slate-200/80 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm transition-size overflow-hidden ${
            vizExpanded ? "w-[44%]" : "max-w-0 w-0"
          }`}>
            {/* Viz header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/80 dark:border-slate-800">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold truncate">{context.title || "Preview"}</h3>
                <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{context.subtitle || "Charts will appear here"}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-3">
                {chartUrl && (
                  <button
                    onClick={() => window.open(chartUrl, "_blank")}
                    className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 transition"
                    title="Open in new tab"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </button>
                )}
                <button
                  onClick={() => setVizExpanded(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 transition"
                  title="Close panel"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            {/* Chart content */}
            <div className="h-[calc(100%-3.5rem)] overflow-auto p-4">
              <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 shadow-sm flex flex-col h-full min-h-[560px]">
                {chartLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative w-10 h-10">
                        <div className="absolute inset-0 rounded-full border-2 border-indigo-100 dark:border-indigo-900" />
                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 animate-spin" />
                      </div>
                      <span className="text-xs text-slate-400 dark:text-slate-500">Creating chart...</span>
                    </div>
                  </div>
                ) : chartUrl ? (
                  <iframe
                    src={chartUrl}
                    title="Superset Chart"
                    className="flex-1 w-full rounded-xl"
                    style={{ border: 0, minHeight: 560 }}
                    allow="fullscreen"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-slate-50 dark:bg-slate-700/40 flex items-center justify-center">
                        <svg className="w-7 h-7 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-slate-400 dark:text-slate-500">No chart yet</p>
                      <p className="text-xs text-slate-400/60 dark:text-slate-500/60 mt-1">Ask something in the chat</p>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </aside>
        </main>
      </div>

      {/* ── Overlay ── */}
      {overlay.open && (
        <div className="fixed inset-0 z-50 fade-in" onClick={(e) => { if (e.target === e.currentTarget) setOverlay({ open: false, title: "", content: "" }); }}>
          <div className="absolute inset-0 bg-black/25 dark:bg-black/50 backdrop-blur-sm" />
          <div className="absolute left-0 top-0 h-full w-[360px] max-w-[85%] bg-white dark:bg-slate-900 shadow-2xl p-5 overflow-auto border-r border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold">{overlay.title}</h3>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition"
                onClick={() => setOverlay({ open: false, title: "", content: "" })}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {overlay.type === "connections" ? (
              <div className="space-y-4">
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Point Vizaura at any Superset instance. Databases, datasets and chart creation
                  will all use whichever one is connected.
                </p>

                {/* Active connection */}
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">Apache Superset</p>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            activeConn?.custom
                              ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                          }`}
                        >
                          {activeConn?.custom ? "Custom" : "Default"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                        {activeConn ? `Connected · ${activeConn.base_url}` : "Loading…"}
                      </p>
                    </div>
                    {activeConn?.base_url && (
                      <a
                        href={activeConn.base_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                      >
                        Open
                      </a>
                    )}
                  </div>
                </div>

                {/* Connect form */}
                <div className={`rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800 space-y-3 ${activeConn?.locked ? "opacity-60 pointer-events-none select-none" : ""}`}>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">Connect to another Superset</p>
                    {activeConn?.locked && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 font-medium">
                        <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                        Locked
                      </span>
                    )}
                  </div>
                  {activeConn?.locked && (
                    <p className="text-[11px] text-slate-400">Switching Superset is disabled for this deployment.</p>
                  )}
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">Superset URL</label>
                    <input
                      value={connForm.base_url}
                      onChange={(e) => setConnForm((f) => ({ ...f, base_url: e.target.value }))}
                      placeholder="https://superset.example.com"
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/40"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">Username</label>
                      <input
                        value={connForm.username}
                        onChange={(e) => setConnForm((f) => ({ ...f, username: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/40"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-1">Password</label>
                      <input
                        type="password"
                        value={connForm.password}
                        onChange={(e) => setConnForm((f) => ({ ...f, password: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/40"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={testSupersetConn}
                      disabled={connBusy}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition"
                    >
                      Test
                    </button>
                    <button
                      onClick={connectSuperset}
                      disabled={connBusy}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50 transition"
                    >
                      {connBusy ? "Working…" : "Connect"}
                    </button>
                    {activeConn?.custom && (
                      <button
                        onClick={resetSuperset}
                        disabled={connBusy}
                        className="ml-auto text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline disabled:opacity-50"
                      >
                        Reset to default
                      </button>
                    )}
                  </div>
                </div>

                <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">
                  The connection applies to data (databases, datasets, chart creation). The inline
                  chart-preview embed still points at the local instance.
                </p>
              </div>
            ) : overlay.type === "admin" ? (
              <div className="space-y-6">
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Invite-only access. Add the emails allowed to sign up or log in.
                </p>

                {/* Allowed emails */}
                <div>
                  <p className="font-medium text-sm mb-2">Allowed emails</p>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addAllowedEmail(); }}
                      placeholder="name@company.com"
                      className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/40"
                    />
                    <button
                      onClick={addAllowedEmail}
                      disabled={adminBusy}
                      className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50"
                    >
                      Allow
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {adminData.allowed_emails.length === 0 && (
                      <p className="text-xs text-slate-400">No emails added yet.</p>
                    )}
                    {adminData.allowed_emails.map((email) => (
                      <div key={email} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-800">
                        <span className="text-sm truncate">{email}</span>
                        <button
                          onClick={() => removeAllowedEmail(email)}
                          disabled={adminBusy}
                          className="text-slate-400 hover:text-red-500 shrink-0"
                          title="Remove from allowlist"
                        >
                          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
                    Removing an email blocks that person from logging in (the account itself isn't deleted).
                  </p>
                </div>

                {/* Accounts */}
                <div>
                  <p className="font-medium text-sm mb-2">Accounts ({adminData.users.length})</p>
                  <div className="space-y-1.5">
                    {adminData.users.map((u) => (
                      <div key={u.id} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-800">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm truncate">{u.email}</span>
                            {u.is_admin && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 font-medium">admin</span>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">@{u.username}</span>
                        </div>
                        {!u.is_admin && (
                          <button
                            onClick={() => deleteUserAccount(u.id)}
                            disabled={adminBusy}
                            className="text-xs text-slate-400 hover:text-red-500 shrink-0"
                            title="Delete account"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity — per-user request log */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm">Activity ({adminActivity.length})</p>
                    <button onClick={loadActivity} disabled={activityLoading}
                      className="text-[11px] text-slate-400 hover:text-indigo-500 disabled:opacity-50">Refresh</button>
                  </div>
                  {activityLoading ? (
                    <p className="text-xs text-slate-400">Loading…</p>
                  ) : adminActivity.length === 0 ? (
                    <p className="text-xs text-slate-400">No requests logged yet — this fills in as people use the app.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                      {adminActivity.map((a) => (
                        <div key={a.id} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-800">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium truncate">{a.user_email || "—"}</span>
                            <span className="text-[10px] text-slate-400 shrink-0">{(a.created_at || "").replace("T", " ").slice(0, 16)}</span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 break-words">{a.prompt || "—"}</p>
                          <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full ${a.success ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"}`}>
                            {({ created: "Created chart", modified: "Updated chart", missing_data: "Data not found", no_active_chart: "No active chart", created_dashboard: "Dashboard", qa: "Q&A", needs_clarification: "Needs detail" }[a.action]) || (a.success ? "OK" : "Failed")}
                            {a.chart_id ? ` · #${a.chart_id}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
                    Per-user logging began 2026-08-30. Earlier charts were created via a shared service account and aren't attributable to a person.
                  </p>
                </div>
              </div>
            ) : overlay.type === "help" ? (
              <div className="space-y-5 text-sm text-slate-600 dark:text-slate-300">
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-1.5">Getting started</h4>
                  <ul className="list-disc pl-5 space-y-1 text-[13px]">
                    <li>Pick a <strong>Database</strong> and a <strong>Project</strong> (programme) at the top.</li>
                    <li>In <strong>Chart Creation</strong>, describe the chart in plain English — e.g. <em>"average pain score at each time point"</em>.</li>
                    <li>Use <strong>Q&amp;A</strong> mode to ask what the project contains, what charts you can build, or why something didn't work.</li>
                    <li>Leave <strong>Dataset</strong> on <strong>+ Create New</strong> for a fresh chart; switch <strong>Chart Mode</strong> to <strong>Update</strong> to refine the current one.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-1.5">Tips</h4>
                  <ul className="list-disc pl-5 space-y-1 text-[13px]">
                    <li>If the assistant asks a clarifying question, click one of the suggested chips to continue.</li>
                    <li>Name the questionnaire and time points for best results (e.g. <em>"EQ-5D pain at Baseline, 6 weeks, 12 months"</em>).</li>
                    <li>Press <strong>Stop</strong> to cancel a request mid-way, and <strong>pin</strong> important chats to keep them at the top.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-1.5">What's new</h4>
                  <ul className="list-disc pl-5 space-y-1 text-[13px]">
                    <li>Smarter assistant that understands ZEDOC programmes, questionnaires and time points.</li>
                    <li>More reliable chart building, with specific guidance when a request is unclear.</li>
                    <li>Pin chats, stop a running query, bigger chart view, and a proper dark-mode toggle.</li>
                  </ul>
                </div>
                <p className="text-[12px] text-slate-400 dark:text-slate-500">Found a problem or have an idea? Use <strong>Feedback</strong> in the menu.</p>
              </div>
            ) : overlay.type === "settings" ? (
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Set your preferences.</p>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
                    <div>
                      <p className="font-medium">Dark mode</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Switch between light and dark appearance</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={isDark}
                      onClick={() => setIsDark((v) => !v)}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 ${isDark ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"}`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${isDark ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: overlay.content }} />
            )}
          </div>
        </div>
      )}

      {/* ── Feedback modal ── */}
      {feedbackOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 fade-in" onClick={(e) => { if (e.target === e.currentTarget) closeFeedback(); }}>
          <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-2xl max-h-[88vh] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white shadow-sm shadow-indigo-500/25">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold">Share feedback</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Tell the team what's working, what's not, or ideas to explore.</p>
                </div>
              </div>
              <button onClick={closeFeedback} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 shrink-0">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Feedback tabs — visible to all members (shared ticket view) */}
            <div className="flex gap-4 px-6 pt-3 border-b border-slate-100 dark:border-slate-800">
              {[["compose", "Compose"], ["list", "All feedback"]].map(([v, label]) => (
                <button key={v} onClick={() => { setFbView(v); if (v === "list") loadFeedbackList(); }}
                  className={`pb-2.5 text-sm font-medium border-b-2 -mb-px transition ${fbView === v ? "border-indigo-500 text-indigo-600 dark:text-indigo-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto px-6 py-5">
              {fbView === "list" ? (
                fbListLoading ? (
                  <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Loading…</div>
                ) : fbList.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 text-sm">No feedback yet.</div>
                ) : (
                  <div className="space-y-3">
                    {fbList.map((fb) => {
                      const c = fbCat(fb.category);
                      return (
                        <div key={fb.id} className={`rounded-xl border p-4 ${fb.status === "closed" ? "border-slate-200/60 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-800/20 opacity-70" : fb.status === "reviewed" ? "border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"}`}>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${FB_CLS[c.cls]}`}>
                                <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{c.icon}</svg>
                                {c.short}
                              </span>
                              {fb.status === "closed" && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">Closed</span>
                              )}
                            </div>
                            <span className="text-[11px] text-slate-400 truncate">{fb.user_email} · {new Date(fb.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{fb.message}</p>
                          {fb.attachments?.length > 0 && (
                            <div className="grid grid-cols-2 gap-2 mt-3">
                              {fb.attachments.map((a) => <FbMedia key={a.id} att={a} />)}
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-3 text-xs">
                            {fb.status === "closed" ? (
                              user?.is_admin && (
                                <button onClick={() => toggleReviewed(fb.id)} className="text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium">Reopen</button>
                              )
                            ) : (
                              <>
                                <button onClick={() => toggleReviewed(fb.id)} className="text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium">
                                  {fb.status === "reviewed" ? "↩ Mark as new" : "✓ Mark reviewed"}
                                </button>
                                {user?.is_admin && (
                                  <button onClick={() => closeFeedbackTicket(fb.id)} className="text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium">Close &amp; notify</button>
                                )}
                              </>
                            )}
                            {user?.is_admin && (
                              <button onClick={() => removeFeedback(fb.id)} className="text-slate-400 hover:text-rose-500">Delete</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : fbSent ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">Thanks — sent to the team</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">We read every note. This helps shape what we build next.</p>
                  <div className="flex items-center justify-center gap-2 mt-5">
                    <button onClick={() => { setFbSent(false); setFbCategory("bug"); }} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">Send another</button>
                    <button onClick={closeFeedback} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium">Done</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Category */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">What kind of feedback?</label>
                    <div className="grid grid-cols-2 gap-2">
                      {FB_CATEGORIES.map((c) => (
                        <button key={c.key} onClick={() => setFbCategory(c.key)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm text-left transition ${fbCategory === c.key ? "border-indigo-500 bg-indigo-50/60 dark:bg-indigo-500/10 ring-1 ring-indigo-500/30" : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"}`}>
                          <span className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${FB_CLS[c.cls]}`}>
                            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{c.icon}</svg>
                          </span>
                          <span className={fbCategory === c.key ? "font-medium text-slate-800 dark:text-slate-100" : "text-slate-600 dark:text-slate-300"}>{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Your feedback</label>
                    <textarea rows={5} value={fbMessage} onChange={(e) => setFbMessage(e.target.value)}
                      placeholder="What happened, what you expected, or an idea… Screenshots and screen recordings help a lot."
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500/40 resize-none" />
                  </div>
                  {/* Upload */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Attach images or videos <span className="text-slate-400">(optional)</span></label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setFbDrag(true); }}
                      onDragLeave={() => setFbDrag(false)}
                      onDrop={(e) => { e.preventDefault(); setFbDrag(false); addFbFiles(e.dataTransfer.files); }}
                      onClick={() => fbInputRef.current?.click()}
                      className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${fbDrag ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10" : "border-slate-300 dark:border-slate-600 hover:border-indigo-400"}`}>
                      <svg viewBox="0 0 24 24" className="w-7 h-7 mx-auto text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">Drag &amp; drop, or <span className="text-indigo-600 dark:text-indigo-400 font-medium">browse</span></p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Images &amp; videos · up to {FB_MAX_MB}MB each · {FB_MAX_FILES} max</p>
                      <input ref={fbInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { addFbFiles(e.target.files); e.target.value = ""; }} />
                    </div>
                    {fbFiles.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        {fbFiles.map((f, i) => (
                          <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                            {f.isVideo ? (
                              <div className="relative aspect-video flex items-center justify-center bg-slate-900">
                                <video src={f.url} className="max-h-full max-w-full" />
                                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-white/90 drop-shadow" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                                </span>
                              </div>
                            ) : (
                              <img src={f.url} alt="" className="aspect-video w-full object-cover" />
                            )}
                            <button onClick={(e) => { e.stopPropagation(); removeFbFile(i); }}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                            <p className="absolute bottom-0 inset-x-0 text-[10px] text-white/90 bg-black/40 px-1.5 py-0.5 truncate">{fbFmtSize(f.file.size)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer (compose only) */}
            {fbView === "compose" && !fbSent && (
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-800">
                <p className="text-[11px] text-slate-400 truncate">Sent as {user?.email || "you"}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={closeFeedback} className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
                  <button onClick={submitFeedback} disabled={fbBusy || !fbMessage.trim()}
                    className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2 transition">
                    {fbBusy ? "Sending…" : (<>Send feedback<svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg></>)}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
